import { ErrorOutlineRounded, Refresh } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { OrganizationAPI } from "../../../api";
import PATHS from "../../../components/navigation/_paths";
import { useGlobalOrganizationContext } from "../../../hooks/useGlobalOrganizationContext";
import { BillingState } from "../../../model/OrganizationBillingState";
import OrganizationStripeSetupDialog from "./dialogs/OrganizationStripeSetupDialog";

const OrganizationBillingBanner = () => {
  const { organizationBillingState, organization, refreshOrganization } =
    useGlobalOrganizationContext();
  // get the simplfied billing state
  const currentBillingState = organizationBillingState
    ? organizationBillingState.billing_state
    : BillingState.VALID;

  // variables to check if the organization has a stripe_id
  const hasStripeId = organizationBillingState?.stripe_id != null;

  const [forceRefreshResult, setForceRefreshResult] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [setupStripeDialogOpen, setSetupStripeDialogOpen] =
    useState<boolean>(false);

  const navigate = useNavigate();

  const forceRefreshSubscription = useCallback(() => {
    if (!organization) {
      return;
    }
    setIsLoading(true);
    OrganizationAPI.forceRefreshSubscription(organization.id).then((result) => {
      setForceRefreshResult(result);
      setIsLoading(false);
    });
  }, [organization]);

  return (
    <>
      {currentBillingState != BillingState.VALID && !forceRefreshResult && (
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          padding={1}
          color="white"
          sx={{
            bgcolor: (theme) =>
              currentBillingState == BillingState.INVALID
                ? theme.palette.error.main
                : theme.palette.warning.main,
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: 5,
            }}
          >
            {!isLoading ? (
              <IconButton
                onClick={() => {
                  forceRefreshSubscription();
                }}
                size="small"
                sx={{
                  color: "white",
                }}
              >
                <Refresh />
              </IconButton>
            ) : (
              <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
            )}
          </Box>
          <ErrorOutlineRounded
            sx={{
              marginRight: 1,
            }}
          />
          <Typography>
            {hasStripeId
              ? currentBillingState == BillingState.INVALID
                ? "Your subscription is no longer active!"
                : "Your subscription needs to be renewed soon!"
              : "You do not have an active subscription!"}
            {}
          </Typography>

          {hasStripeId ? (
            <>
              <Button
                variant="outlined"
                color="inherit"
                sx={{
                  py: 0,
                  px: 1,
                  borderRadius: 1,
                  ml: 1,
                }}
                onClick={() => {
                  navigate(PATHS.ORGANIZATION_MANAGE_BILLING.link);
                  forceRefreshSubscription();
                }}
              >
                Manage Subscription
              </Button>
            </>
          ) : (
            <Button
              variant="outlined"
              color="inherit"
              sx={{
                py: 0,
                px: 1,
                borderRadius: 1,
                ml: 1,
              }}
              onClick={() => {
                setSetupStripeDialogOpen(true);
              }}
            >
              Setup Subscription
            </Button>
          )}
        </Box>
      )}
      {organization && (
        <OrganizationStripeSetupDialog
          title={"Setup Stripe Subscription"}
          open={setupStripeDialogOpen}
          handleConfirm={async () => {
            setSetupStripeDialogOpen(false);
            await refreshOrganization(organization.id);
          }}
          handleCancel={() => {
            setSetupStripeDialogOpen(false);
          }}
          organization={organization}
        />
      )}
    </>
  );
};

export default OrganizationBillingBanner;
